import { useModal } from '#app/components/Modals/V2/GlobalModalSystem/useModal.js';
import { useCallback, useMemo, useState } from 'react';

type UseProfileMagicProps = {
  closeMainModal: () => void;
  handlePollingCleanup: () => void;
  handleSubmit: (
    whatMakesYouUnique: string,
    whatServicesDoYouOffer: string,
    onError: (errorType: 'OTHER' | 'RATE_LIMIT' | null) => void,
  ) => void;
  lastInput: null | {
    whatMakesYouUnique: string;
    whatServicesDoYouOffer: string;
  };
  onDismissError: () => void;
  openMainModal: () => void;
};

export const useProfileMagicErrorModal = ({
  closeMainModal,
  handlePollingCleanup,
  handleSubmit,
  lastInput,
  onDismissError,
  openMainModal,
}: UseProfileMagicProps) => {
  const [errorModalType, setErrorModalType] = useState<
    ('OTHER' | 'RATE_LIMIT') | null
  >(null);

  const { closeModal: closeErrorModal, openModal: openErrorModal } = useModal({
    identifier: 'ProfileMagicInputErrorModal',
    modalProps: {
      errorType: errorModalType ?? 'OTHER',
      onDismiss: () => {
        setErrorModalType(null);
        onDismissError();
        closeErrorModal();
      },
      onRetry: () => {
        closeErrorModal();

        handleSubmit(
          lastInput?.whatMakesYouUnique ?? '',
          lastInput?.whatServicesDoYouOffer ?? '',
          (errorType) => {
            // close the main modal
            closeMainModal();

            // stop polling
            handlePollingCleanup();

            if (errorType === 'RATE_LIMIT') {
              setErrorModalType('RATE_LIMIT');
              openErrorModal();
            } else if (errorType === 'OTHER') {
              setErrorModalType('OTHER');
              openErrorModal();
            }
          },
        );

        // re-open the main modal so we see the loading animation
        openMainModal();
      },
    },
  });

  const handleErrorModalOpen = useCallback(
    (errorType: 'OTHER' | 'RATE_LIMIT') => {
      setErrorModalType(errorType);
      handlePollingCleanup();

      // close the main modal
      closeMainModal();

      // open the error modal
      openErrorModal();
    },
    [closeMainModal, handlePollingCleanup, openErrorModal],
  );

  return useMemo(
    () => ({
      handleErrorModalOpen,
      setErrorModalType,
    }),
    [handleErrorModalOpen],
  );
};
